import React, { useContext, useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Box,
  Collapse,
  ListSubheader,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../FirebaseAuth";
import { Firestore } from "../../../components/FirebaseAuth/firebase";
import { isKWALLAdmin } from "../../../utils/index";
import { LOCAL_STORAGE_CURRENT_ORG, getLocalStorage } from "../../../utils/localStorage";
import {
  getDoc,
  doc,
  where,
  query,
  collection,
  getDocs,
} from "firebase/firestore";
import "./index.css";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WebIcon from "@mui/icons-material/Web";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SyncIcon from "@mui/icons-material/Sync";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GridViewIcon from "@mui/icons-material/GridView";
import FolderIcon from "@mui/icons-material/Folder";
import PaidIcon from "@mui/icons-material/Paid";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const AppMenu = () => {
  const theme = useTheme();
  const initialUserState = {
    isLoading: true,
    data: null,
  };

  const { pathname } = useLocation();
  const { authUser, isDrawerOpen } = useContext(AuthContext);
  const [userData, setUserData] = useState(initialUserState);
  const [isOwner, setIsOwner] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(
    getLocalStorage(LOCAL_STORAGE_CURRENT_ORG)
  );

  // Manage collapsible sections
  const [openSections, setOpenSections] = useState({
    dashboard: false,
    user: true,
    admin: false,
  });

  const handleToggleSection = (section) => {
    setOpenSections({
      ...openSections,
      [section]: !openSections[section],
    });
  };

  async function getUserData() {
    try {
      const userDocRef = doc(Firestore, "users", authUser.user.uid);
      const refUser = await getDoc(userDocRef);
      const userData = await refUser.data();
      setUserData({
        data: userData,
        isLoading: false,
      });

      const organizationRef = query(
        collection(Firestore, "accounts"),
        where("owner", "==", authUser.user.uid)
      );

      // Fetch documents that match the query
      const organizationData = await getDocs(organizationRef);
      if (!organizationData.empty) {
        setIsOwner(true);
      }
    } catch (ex) {
      console.log("Error fetching user data:", ex);
    }
  }

  useEffect(() => {
    getUserData();
    //eslint-disable-next-line
  }, []);

  // Update currentOrganization when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentOrganization(getLocalStorage(LOCAL_STORAGE_CURRENT_ORG));
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for manual localStorage updates
    const interval = setInterval(() => {
      const stored = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);
      if (JSON.stringify(stored) !== JSON.stringify(currentOrganization)) {
        setCurrentOrganization(stored);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentOrganization]);

  // Check if a menu item is active based on pathname
  const isActive = (path) => {
    if (typeof path === "string") {
      return pathname.includes(path);
    } else if (Array.isArray(path)) {
      return path.some((p) => pathname.includes(p));
    }
    return false;
  };

  // Style for selected menu items
  const getSelectedStyle = (active) => ({
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.primary.main : "rgb(33, 37, 41)",
  });

  // Icons color when selected
  const getIconStyle = (active) => ({
    color: active ? theme.palette.primary.main : "inherit",
  });

  // Section header style
  const sectionStyle = {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    lineHeight: "16px",
    padding: "16px 24px 8px",
  };

  // Empty div for when drawer is closed
  if (!isDrawerOpen) {
    return (
      <List
        sx={{
          p: 1,
          "& .MuiListItemButton-root": {
            borderRadius: 2,
            mb: 0.5,
            pl: 1.5,
          },
        }}
      >
        <ListItem button component={Link} to="/" sx={{ borderRadius: 2 }}>
          <ListItemIcon>
            <DashboardIcon
              style={getIconStyle(
                isActive("/") &&
                  !isActive([
                    "/marketplace",
                    "/organization",
                    "/templates",
                    "/profile",
                    "/log",
                    "/planLists",
                    "/serviceLists",
                  ])
              )}
            />
          </ListItemIcon>
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/marketplace"
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon>
            <ShoppingCartIcon style={getIconStyle(isActive("/marketplace"))} />
          </ListItemIcon>
        </ListItem>

        <Divider sx={{ my: 1.5 }} />

        <ListItem
          button
          component={Link}
          to="/user/profile"
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon>
            <AccountCircleIcon style={getIconStyle(isActive("/profile"))} />
          </ListItemIcon>
        </ListItem>

        {isOwner && (
          <ListItem
            button
            component={Link}
            to="/user/organization"
            sx={{ borderRadius: 2 }}
          >
            <ListItemIcon>
              <GroupWorkIcon
                style={getIconStyle(
                  isActive("/organization") && !isActive("organization_list")
                )}
              />
            </ListItemIcon>
          </ListItem>
        )}

        <ListItem
          button
          component={Link}
          to="/organization/switch-organization"
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon>
            <SyncIcon style={getIconStyle(isActive("/switch"))} />
          </ListItemIcon>
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/user/log"
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon>
            <ListAltIcon style={getIconStyle(isActive("/log"))} />
          </ListItemIcon>
        </ListItem>

        {!userData.isLoading && isKWALLAdmin(userData.data?.role) && (
          <>
            <Divider sx={{ my: 1.5 }} />

            <ListItem
              button
              component={Link}
              to="/organization_list"
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon>
                <ApartmentIcon
                  style={getIconStyle(isActive("/organization_list"))}
                />
              </ListItemIcon>
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/planLists"
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon>
                <PaidIcon style={getIconStyle(isActive("/planLists"))} />
              </ListItemIcon>
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/serviceLists"
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon>
                <SupportAgentIcon
                  style={getIconStyle(isActive("/serviceLists"))}
                />
              </ListItemIcon>
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/global-templates-list"
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon>
                <WebIcon style={getIconStyle(isActive("/templates"))} />
              </ListItemIcon>
            </ListItem>
          </>
        )}
      </List>
    );
  }

  return (
    <List
      component="nav"
      sx={{
        p: 1,
        pt: { xs: 8, md: 0 },
        "& .MuiListItemButton-root": {
          borderRadius: 2,
          mb: 0.5,
          py: 1,
          px: 2,
        },
      }}
    >
      {/* MAIN NAVIGATION */}
      <ListSubheader
        sx={{
          ...sectionStyle,
          bgcolor: "transparent",
          "&.MuiListSubheader-root": {
            lineHeight: "16px",
          },
        }}
      >
        Navigation
      </ListSubheader>

      {/* DASHBOARD SECTION */}
      <ListItem
        button
        onClick={() => handleToggleSection("dashboard")}
        sx={{ borderRadius: 2 }}
        selected={
          isActive("/") ||
          (currentOrganization?.id && (
            isActive(`/organization/${currentOrganization.id}/all-collections`) ||
            isActive(`/organization/${currentOrganization.id}/all-sites`)
          ))
        }
      >
        <ListItemIcon>
          <DashboardIcon
            style={getIconStyle(
              isActive("/") ||
              (currentOrganization?.id && (
                isActive(`/organization/${currentOrganization.id}/all-collections`) ||
                isActive(`/organization/${currentOrganization.id}/all-sites`)
              ))
            )}
          />
        </ListItemIcon>
        <ListItemText
          primary="Dashboard"
          primaryTypographyProps={{
            variant: "body2",
            style: getSelectedStyle(
              isActive("/") ||
              (currentOrganization?.id && (
                isActive(`/organization/${currentOrganization.id}/all-collections`) ||
                isActive(`/organization/${currentOrganization.id}/all-sites`)
              ))
            ),
          }}
        />
        {openSections.dashboard ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </ListItem>

      <Collapse in={openSections.dashboard} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem
            button
            component={Link}
            to="/"
            sx={{ pl: 4, borderRadius: 2 }}
            selected={
              isActive("/") &&
              !isActive([
                "/marketplace",
                "/templates",
                "/profile",
                "/log",
                "/planLists",
                "/serviceLists",
                "/organization",
              ])
            }
          >
            <ListItemIcon>
              <DashboardIcon
                style={getIconStyle(
                  isActive("/") &&
                  !isActive([
                    "/marketplace",
                    "/templates",
                    "/profile",
                    "/log",
                    "/planLists",
                    "/serviceLists",
                    "/organization",
                  ])
                )}
              />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              primaryTypographyProps={{
                variant: "body2",
                style: getSelectedStyle(
                  isActive("/") &&
                  !isActive([
                    "/marketplace",
                    "/templates",
                    "/profile",
                    "/log",
                    "/planLists",
                    "/serviceLists",
                    "/organization",
                  ])
                ),
              }}
            />
          </ListItem>

          {currentOrganization?.id && (
            <>
              <ListItem
                button
                component={Link}
                to={`/organization/${currentOrganization.id}/all-collections`}
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive(`/organization/${currentOrganization.id}/all-collections`)}
              >
                <ListItemIcon>
                  <FolderIcon
                    style={getIconStyle(isActive(`/organization/${currentOrganization.id}/all-collections`))}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="All Collections"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive(`/organization/${currentOrganization.id}/all-collections`)),
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to={`/organization/${currentOrganization.id}/all-sites`}
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive(`/organization/${currentOrganization.id}/all-sites`)}
              >
                <ListItemIcon>
                  <GridViewIcon
                    style={getIconStyle(isActive(`/organization/${currentOrganization.id}/all-sites`))}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="All Sites"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive(`/organization/${currentOrganization.id}/all-sites`)),
                  }}
                />
              </ListItem>
            </>
          )}
        </List>
      </Collapse>

      <ListItem
        button
        component={Link}
        to="/marketplace"
        sx={{ borderRadius: 2 }}
        selected={isActive("/marketplace")}
      >
        <ListItemIcon>
          <ShoppingCartIcon style={getIconStyle(isActive("/marketplace"))} />
        </ListItemIcon>
        <ListItemText
          primary="Marketplace"
          primaryTypographyProps={{
            variant: "body2",
            style: getSelectedStyle(isActive("/marketplace")),
          }}
        />
      </ListItem>

      {/* USER SECTION */}
      <ListItem
        button
        onClick={() => handleToggleSection("user")}
        sx={{
          borderRadius: 2,
          mt: 1,
        }}
      >
        <ListItemIcon>
          <AccountCircleIcon />
        </ListItemIcon>
        <ListItemText
          primary="User"
          primaryTypographyProps={{
            variant: "body2",
            fontWeight: 500,
          }}
        />
        {openSections.user ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </ListItem>

      <Collapse in={openSections.user} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem
            button
            component={Link}
            to="/user/profile"
            sx={{ pl: 4, borderRadius: 2 }}
            selected={isActive("/profile")}
          >
            <ListItemIcon>
              <PeopleAltIcon style={getIconStyle(isActive("/profile"))} />
            </ListItemIcon>
            <ListItemText
              primary="Profile"
              primaryTypographyProps={{
                variant: "body2",
                style: getSelectedStyle(isActive("/profile")),
              }}
            />
          </ListItem>

          {isOwner && (
            <ListItem
              button
              component={Link}
              to="/user/organization"
              sx={{ pl: 4, borderRadius: 2 }}
              selected={
                isActive("/organization") && !isActive("organization_list")
              }
            >
              <ListItemIcon>
                <GroupWorkIcon
                  style={getIconStyle(
                    isActive("/organization") && !isActive("organization_list")
                  )}
                />
              </ListItemIcon>
              <ListItemText
                primary="My Organization"
                primaryTypographyProps={{
                  variant: "body2",
                  style: getSelectedStyle(
                    isActive("/organization") && !isActive("organization_list")
                  ),
                }}
              />
            </ListItem>
          )}

          <ListItem
            button
            component={Link}
            to="/organization/switch-organization"
            sx={{ pl: 4, borderRadius: 2 }}
            selected={isActive("/switch")}
          >
            <ListItemIcon>
              <SyncIcon style={getIconStyle(isActive("/switch"))} />
            </ListItemIcon>
            <ListItemText
              primary="Switch Organization"
              primaryTypographyProps={{
                variant: "body2",
                style: getSelectedStyle(isActive("/switch")),
              }}
            />
          </ListItem>

          <ListItem
            button
            component={Link}
            to="/user/log"
            sx={{ pl: 4, borderRadius: 2 }}
            selected={isActive("/log")}
          >
            <ListItemIcon>
              <ListAltIcon style={getIconStyle(isActive("/log"))} />
            </ListItemIcon>
            <ListItemText
              primary="Activity Logs"
              primaryTypographyProps={{
                variant: "body2",
                style: getSelectedStyle(isActive("/log")),
              }}
            />
          </ListItem>
        </List>
      </Collapse>

      {/* ADMIN SECTION - Only visible to KWALL admins */}
      {!userData.isLoading && isKWALLAdmin(userData.data?.role) && (
        <>
          <ListItem
            button
            onClick={() => handleToggleSection("admin")}
            sx={{
              borderRadius: 2,
              mt: 1,
            }}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Admin"
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 500,
              }}
            />
            {openSections.admin ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </ListItem>

          <Collapse in={openSections.admin} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={Link}
                to="/organization_list"
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive("/organization_list")}
              >
                <ListItemIcon>
                  <ApartmentIcon
                    style={getIconStyle(isActive("/organization_list"))}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Organizations"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive("/organization_list")),
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/planLists"
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive("/planLists")}
              >
                <ListItemIcon>
                  <PaidIcon style={getIconStyle(isActive("/planLists"))} />
                </ListItemIcon>
                <ListItemText
                  primary="Manage Plans"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive("/planLists")),
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/serviceLists"
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive("/serviceLists")}
              >
                <ListItemIcon>
                  <SupportAgentIcon
                    style={getIconStyle(isActive("/serviceLists"))}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Manage Services"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive("/serviceLists")),
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/global-templates-list"
                sx={{ pl: 4, borderRadius: 2 }}
                selected={isActive("/templates")}
              >
                <ListItemIcon>
                  <WebIcon style={getIconStyle(isActive("/templates"))} />
                </ListItemIcon>
                <ListItemText
                  primary="Templates"
                  primaryTypographyProps={{
                    variant: "body2",
                    style: getSelectedStyle(isActive("/templates")),
                  }}
                />
              </ListItem>
            </List>
          </Collapse>
        </>
      )}
    </List>
  );
};

export default AppMenu;
